import Dialog from "../../classes/Dialog.js";
import SelectDialog from "../../classes/SelectDialog.js";
import Pad from "../../classes/Pad.js";
import Template from "../../classes/Template.js";
import TokenStore from "../../classes/TokenStore.js";
import TokenDialog from "../../classes/TokenDialog.js";
import Names from "../../classes/Names.js";
import {
    empty,
    identify,
    lookup,
    lookupOne,
    lookupOneCached,
    replaceContentsMany
} from "../../utils/elements.js";
import {
    isOrphanId
} from "../../utils/arrays.js";
import {
    isSpecialTeam
} from "../../constants/teams.js";
import {
    gameObserver,
    logObserver,
    tokenObserver
} from "../../constants/observers.js";

const pad = lookupOneCached(".js--pad").pad;
const recentReminders = lookupOneCached("#character-show-reminders");
const characterShowDialog = Dialog.create(lookupOneCached("#character-show"));
const tokenDialog = TokenDialog.get();
tokenDialog.setEntryTemplate(new Template(lookupOne("#token-entry-template")));

// Set up the token dialog when a character token is clicked.
tokenObserver.on("character-click", ({ detail }) => {

    const {
        element
    } = detail;
    const character = pad.getCharacterByToken(element);

    characterShowDialog.getElement().dataset.token = `#${identify(element)}`;
    lookupOneCached("#character-show-name").textContent = character.getName();
    lookupOneCached("#character-show-ability").textContent = character.getAbility();
    recentReminders.dataset.coords = JSON.stringify(pad.getTokenPosition(element));

    characterShowDialog.show();

});

// Update the recently-added-reminders list as a reminder is added.
tokenObserver.on("reminder-add", ({ detail }) => {

    const {
        reminder
    } = detail;
    const id = reminder.getId();
    const items = lookup(".js--reminder-list--item", recentReminders);

    const existing = items.find(({ dataset }) => dataset.reminderId === id);

    if (existing && existing === items[0]) {
        return;
    }

    if (items.length > 2 || (existing && existing !== items[0])) {
        (existing || items[items.length - 1]).remove();
    }

    recentReminders.prepend(reminder.drawList());

});

function getToken(target) {
    return lookupOne(target.closest("[data-token]").dataset.token);
}

function hideDialog(target) {
    Dialog.create(target.closest(".dialog")).hide();
}

TokenStore.ready(() => {

    // Show a token as it's clicked from the "show tokens" dialog.
    lookupOne("#character-show-token").addEventListener("click", ({ target }) => {

        tokenDialog.setIds([
            pad.getCharacterByToken(getToken(target)).getId()
        ]);
        tokenDialog.show();

        hideDialog(target);

    });

});

const deathNoteDialog = Dialog.create(lookupOneCached("#death-note"));
const deathReasonSelect = lookupOne(".js--death-reason");
const deathReasonCustomWrapper = lookupOne(".js--death-reason-custom-wrapper");
const deathReasonCustomInput = lookupOne(".js--death-reason-custom");

const updateDeathReasonCustom = () => {
    if (!deathReasonSelect || !deathReasonCustomWrapper) {
        return;
    }
    const isOther = deathReasonSelect.value === "other";
    deathReasonCustomWrapper.hidden = !isOther;
    if (isOther && deathReasonCustomInput) {
        deathReasonCustomInput.focus();
    }
};

if (deathReasonSelect) {
    deathReasonSelect.addEventListener("change", updateDeathReasonCustom);
}

lookupOne("#character-shroud-toggle").addEventListener("click", ({ target }) => {

    const token = getToken(target);
    const character = pad.getCharacterByToken(token);
    const wasDead = character.getIsDead();

    pad.toggleDeadByToken(token);
    hideDialog(target);

    if (!wasDead) {
        lookupOne(".js--death-player-name").value = pad.getPlayerName(character) || character.getName();
        lookupOne(".js--death-note-form").dataset.characterId = character.getId();
        deathNoteDialog.getElement().dataset.token = `#${identify(token)}`;
        deathReasonSelect.value = "execution";
        if (deathReasonCustomInput) {
            deathReasonCustomInput.value = "";
        }
        updateDeathReasonCustom();
        deathNoteDialog.show();
    }

});

lookupOne(".js--death-note-form").addEventListener("submit", (event) => {
    event.preventDefault();
    const reason = deathReasonSelect.value;
    const reasonCustom = deathReasonCustomInput?.value.trim() || "";
    logObserver.trigger("character-died", {
        playerName: lookupOne(".js--death-player-name").value,
        characterId: event.currentTarget.dataset.characterId,
        reason,
        reasonCustom
    });
    deathNoteDialog.hide();

    const animationEvents = {
        execution: "character-executed",
        demon: "character-killed-by-demon"
    };

    if (animationEvents[reason]) {

        const button = getToken(deathNoteDialog.getElement());

        tokenObserver.trigger(animationEvents[reason], {
            element: Pad.getToken(button)
        });

    }
});

lookupOne("#character-rotate").addEventListener("click", ({ target }) => {

    pad.rotateByToken(getToken(target));
    hideDialog(target);

});

const activeAbilities = new Set(["poisoner"]);
const abilityTargetDialog = Dialog.create(lookupOne("#ability-target"));
const abilityTargetSelect = lookupOne("#ability-target-select");
let abilitySource = null;
let abilityTargets = [];

["poisoned", "drunk"].forEach((condition) => {
    lookupOne(`#character-${condition}`).addEventListener("change", ({ target }) => {
        const character = pad.getCharacterByToken(getToken(target));
        const conditions = { ...character.conditions, [condition]: target.checked };
        if (condition === "poisoned") {
            conditions.manualPoisoned = target.checked;
            if (!target.checked) {
                conditions.poisonSources = [];
            }
        }
        pad.setConditions(character, conditions);
        if (target.checked) {
            tokenObserver.trigger("character-condition-applied", { element: Pad.getToken(getToken(target)), kind: condition });
        }
        hideDialog(target);
    });
});

characterShowDialog.on(Dialog.SHOW, () => {

    const token = getToken(characterShowDialog.getElement());
    const character = pad.getCharacterByToken(token);

    lookupOne("#character-use-ability").disabled = !activeAbilities.has(character.getId());
    ["poisoned", "drunk"].forEach((condition) => {
        lookupOne(`#character-${condition}`).checked = Boolean(character.conditions?.[condition]);
    });

    setGhostButtonState(character);

    characterNameInput.value = pad.getPlayerNameForToken(token);
    lookupOneCached("#character-show-orphan").hidden = (
        !token.classList.contains("is-orphan")
    );

});

lookupOne("#character-use-ability").addEventListener("click", ({ target }) => {
    abilitySource = pad.getCharacterByToken(getToken(target));
    if (!activeAbilities.has(abilitySource.getId())) {
        return;
    }
    abilityTargets = [...pad.characters];
    abilityTargetSelect.replaceChildren();
    abilityTargets.forEach(({ character }, index) => {
        const option = document.createElement("option");
        option.value = index;
        option.textContent = `${pad.getPlayerName(character) || "Без имени"} — ${character.getName()}`;
        abilityTargetSelect.append(option);
    });
    hideDialog(target);
    abilityTargetDialog.show();
});

function clearPoisonSource(sourceId) {
    pad.characters.forEach(({ character }) => {
        const previous = character.conditions || {};
        if (!previous.poisonSources?.includes(sourceId)) {
            return;
        }
        const poisonSources = previous.poisonSources.filter((id) => id !== sourceId);
        pad.setConditions(character, {
            ...previous,
            poisonSources,
            poisoned: Boolean(previous.manualPoisoned || poisonSources.length)
        });
    });
    pad.reminders
        .filter(({ reminder }) => reminder.conditions?.poisonSourceId === sourceId)
        .forEach(({ reminder }) => pad.removeReminder(reminder));
}

function positionReminderNearToken(token, target) {
    const coords = pad.getTokenPosition(target.token);
    const targetRect = target.token.getBoundingClientRect();
    const reminderRect = token.getBoundingClientRect();
    const targetCentre = {
        x: coords.x + targetRect.width / 2,
        y: coords.y + targetRect.height / 2
    };
    const padCentre = pad.characters.reduce((centre, { token: characterToken }) => {
        const position = pad.getTokenPosition(characterToken);
        const rect = characterToken.getBoundingClientRect();
        centre.x += position.x + rect.width / 2;
        centre.y += position.y + rect.height / 2;
        return centre;
    }, { x: 0, y: 0 });
    padCentre.x /= pad.characters.length;
    padCentre.y /= pad.characters.length;
    let dx = padCentre.x - targetCentre.x;
    let dy = padCentre.y - targetCentre.y;
    const length = Math.hypot(dx, dy) || 1;
    dx /= length;
    dy /= length;
    const distance = (targetRect.width + reminderRect.width) / 2;
    pad.moveToken(
        token,
        targetCentre.x + dx * distance - reminderRect.width / 2,
        targetCentre.y + dy * distance - reminderRect.width / 2
    );
}

lookupOne("#ability-target-form").addEventListener("submit", (event) => {
    event.preventDefault();
    const source = abilitySource;
    const target = abilityTargets[abilityTargetSelect.value];
    if (!source || !target || !pad.characters.includes(target)
        || !pad.characters.some(({ character }) => character === source)) {
        return;
    }
    const conditions = source.conditions || {};
    const impaired = [
        conditions.poisoned && "отравленным",
        conditions.drunk && "опьянённым"
    ].filter(Boolean);
    const describe = (character) => `${character.getName()} (${pad.getPlayerName(character) || "Без имени"})`;
    const text = impaired.length
        ? `${describe(source)} пытается применить способность к ${describe(target.character)}, будучи ${impaired.join(" и ")}.`
        : `${describe(source)} применяет способность к ${describe(target.character)}: отравление.`;
    if (!impaired.length) {
        const reminder = source.getReminders()[0]?.clone();
        if (!reminder) {
            return;
        }
        const sourceId = conditions.sourceId || `poisoner-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        pad.setConditions(source, { ...conditions, sourceId });
        clearPoisonSource(sourceId);
        reminder.conditions = { poisonSourceId: sourceId };
        const { token } = pad.addReminder(reminder);
        tokenObserver.trigger("conditions-change", { character: reminder, conditions: reminder.conditions });
        positionReminderNearToken(token, target);
        const previous = target.character.conditions || {};
        pad.setConditions(target.character, {
            ...previous,
            manualPoisoned: previous.manualPoisoned ?? Boolean(previous.poisoned && !previous.poisonSources?.length),
            poisonSources: [...(previous.poisonSources || []), sourceId],
            poisoned: true
        });
        tokenObserver.trigger("character-condition-applied", { element: Pad.getToken(target.token), kind: "poisoned" });
    }
    logObserver.trigger("ability-used", { text });
    abilityTargetDialog.hide();
});

const characterListDialog = SelectDialog.get();
characterListDialog.addProcess({

    // The generic process: add a token to the pad when the icon is clicked.

    click(tokenId) {

        TokenStore.ready((tokenStore) => {
            pad.addCharacter(tokenStore.getCharacterClone(tokenId));
        });
        characterListDialog.hide();

    }

});

// The process that will replace one token in the grimoire with another one.
const replaceOnPadProcess = {

    data: null,

    click(tokenId) {

        TokenStore.ready((tokenStore) => {

            const {
                character,
                token: newToken
            } = pad.addCharacter(tokenStore.getCharacterClone(tokenId));
            const {
                data
            } = this;

            if (data) {

                const {
                    token,
                    coords: {
                        x,
                        y,
                        z
                    }
                } = data;

                const oldCharacter = pad.getCharacterByToken(lookupOne(token));
                pad.toggleDead(character, oldCharacter.getIsDead());
                pad.rotate(character, oldCharacter.getIsUpsideDown());
                pad.setPlayerName(character, pad.getPlayerName(oldCharacter));
                pad.removeCharacter(oldCharacter);
                pad.moveToken(newToken, x, y, z);

            }

        });

        characterListDialog.hide();

    },

    hide() {
        this.data = null;
        characterListDialog.removeProcess(replaceOnPadProcess);
    }

};

lookupOne("#character-replace").addEventListener("click", ({ target }) => {

    const token = getToken(target);
    replaceOnPadProcess.data = {
        coords: pad.getTokenPosition(token),
        token: `#${identify(token)}`
    };
    characterListDialog.addProcess(replaceOnPadProcess);
    characterListDialog.show();
    hideDialog(target);

});

// The process that will add another token to the token dialog.
const addToDialogProcess = {

    click(tokenId) {
        tokenDialog.addId(tokenId);
        tokenDialog.show();
        characterListDialog.hide();
    },

    hide() {
        characterListDialog.removeProcess(addToDialogProcess);
    }

};

lookupOne(".js--token--add").addEventListener("click", () => {
    characterListDialog.addProcess(addToDialogProcess);
    characterListDialog.show();
    tokenDialog.hide();
});

const characterNameInput = lookupOne("#character-name-input");
lookupOne("#character-name").addEventListener("click", ({ target }) => {

    const {
        value
    } = characterNameInput;
    const name = (value || "").trim();

    pad.setPlayerNameForToken(getToken(target), name);
    hideDialog(target);

});

const ghostVoteButton = lookupOneCached("#character-ghost-vote");

function setGhostButtonState(character) {

    ghostVoteButton.disabled = (
        !character.getIsDead() || !character.getHasGhostVote()
    );

}

ghostVoteButton.addEventListener("click", ({ target }) => {

    const token = getToken(target);
    pad.setGhostVoteForToken(token, false);

    const character = pad.getCharacterByToken(token);
    setGhostButtonState(character);

    hideDialog(target);

});

characterShowDialog.on(Dialog.HIDE, () => {
    characterNameInput.value = characterNameInput.defaultValue;
});

lookupOne("#character-remove").addEventListener("click", ({ target }) => {

    pad.removeCharacterByToken(getToken(target));
    hideDialog(target);

});

// List of tokens.
// TODO: could we use character.drawList() here?

const tokenListTemplate = Template.create(lookupOne("#token-list-template"));
const tokenList = lookupOne("#token-list__list");

const renderTokenList = ({ detail }) => {

    const teams = ["townsfolk", "outsider", "minion", "demon", "traveller", "fabled", "loric"];
    const rank = (character) => {
        const index = teams.indexOf(character.getTeam());
        return index === -1 ? teams.length : index;
    };
    const characters = detail.characters.filter((character) => {
        const team = character.getTeam();
        return detail.imported || team !== "traveller";
    }).sort((first, second) => rank(first) - rank(second));

    replaceContentsMany(
        tokenList,
        characters.map((character) => tokenListTemplate.draw({
            ".js--token-list--button"(element) {
                element.dataset.tokenId = character.getId();
            },
            ".js--token-list--token"(element) {
                element.append(character.drawToken());
            }
        }))
    );
    lookupOneCached("#show-tokens").disabled = false;

};

gameObserver.on("characters-selected", renderTokenList);
gameObserver.on("imported-roles", ({ detail }) => renderTokenList({ detail: { ...detail, imported: true } }));

// #131 - highlight any orphan character and reminder tokens.
gameObserver.on("characters-selected", ({ detail }) => {

    TokenStore.ready((store) => {

        pad.characters.forEach(({ character, token }) => {

            if (isSpecialTeam(character.getTeam())) {
                return;
            }

            token.classList.toggle(
                "is-orphan",
                isOrphanId(detail.characters, character.getId())
            );

        });

        pad.reminders.forEach(({ reminder, token }) => {

            const character = store.getCharacter(reminder.getCharacterId());
            const id = character?.getId();

            if (
                !id // Ignore the empty ID - it's a generic character.
                || isSpecialTeam(character.getTeam())
            ) {
                return;
            }

            token.classList.toggle("is-orphan", isOrphanId(detail.characters, id));

        });

    });

});

TokenStore.ready((tokenStore) => {

    tokenDialog.setTokenStore(tokenStore);
    const tokenListDialog = Dialog.create(lookupOne("#token-list"));

    tokenList.addEventListener("click", ({ target }) => {

        const button = target.closest("[data-token-id]");

        if (!button) {
            return;
        }

        tokenDialog.setIds([button.dataset.tokenId]);
        tokenDialog.show();
        tokenListDialog.hide();

    });

});

// Update the list of suggested names that can be set when a token is drawn.

const names = Names.create();

names.on("names-added", () => {

    replaceContentsMany(
        lookupOneCached("#player-name-options"),
        names.drawList()
    );
    replaceContentsMany(
        lookupOneCached("#character-name-input-options"),
        names.drawList()
    );

});

names.on("names-cleared", () => {

    empty(lookupOneCached("#player-name-options"));
    empty(lookupOneCached("#character-name-input-options"));

});
